import { useParams, Link } from 'react-router-dom';
import { articleMap, getRelatedArticles } from '@/mocks/articles';
import { categoryMap } from '@/mocks/categories';
import SchemaOrg from '@/components/feature/SchemaOrg';
import Breadcrumb from '@/components/feature/Breadcrumb';
import NotFound from '@/pages/NotFound';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? articleMap[slug] : undefined;

  if (!article) {
    return <NotFound />;
  }

  const category = categoryMap[article.category];
  const relatedArticles = getRelatedArticles(article.slug, article.category, 3);

  const schemaData = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.date,
    url: `https://mastinfo.github.io/article/${article.slug}`,
    inLanguage: 'zh-CN',
    publisher: {
      '@type': 'Organization',
      name: '自慰方式完全指南',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://mastinfo.github.io/article/${article.slug}`,
    },
  };

  return (
    <>
      <SchemaOrg type="Article" additionalData={schemaData} />

      <div className="min-h-screen bg-background-50 pt-16">
        {/* Article Header */}
        <section className="w-full bg-gradient-to-br from-primary-50 via-background-50 to-accent-50/30 py-10 md:py-14">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: category?.name || '', href: `/category/${article.category}` },
                { label: article.title },
              ]}
            />
            <div className="mt-6">
              <div className="flex items-center gap-3 mb-4 flex-wrap">
                {category && (
                  <Link
                    to={`/category/${category.slug}`}
                    className="text-xs font-medium text-primary-600 bg-primary-50 px-3 py-1 rounded-full whitespace-nowrap hover:bg-primary-100 transition-colors"
                  >
                    {category.name}
                  </Link>
                )}
                <span className="text-xs text-foreground-400 whitespace-nowrap">{article.readTime}</span>
                <span className="text-xs text-foreground-400 whitespace-nowrap">{article.date}</span>
              </div>
              <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 leading-tight" style={{ fontFamily: 'var(--font-heading)' }}>
                {article.title}
              </h1>
              <div className="mt-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
                  <i className="ri-user-line text-primary-600"></i>
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground-900">{article.author}</p>
                  <p className="text-xs text-foreground-400">医学审核 · 科普作者</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Feature Image */}
        <section className="w-full px-4 md:px-6 py-6">
          <div className="max-w-3xl mx-auto">
            <div className="relative h-64 md:h-80 rounded-2xl overflow-hidden">
              <img
                src={article.imageUrl}
                alt={article.title}
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
        </section>

        {/* Article Content */}
        <article className="w-full px-4 md:px-6 py-6 md:py-10">
          <div className="max-w-3xl mx-auto">
            <div className="bg-white rounded-2xl border border-background-200/70 p-6 md:p-10">
              <div
                className="article-content"
                dangerouslySetInnerHTML={{ __html: article.content }}
              />
            </div>

            {/* Tags */}
            <div className="mt-6 flex items-center gap-2 flex-wrap">
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1.5 bg-secondary-50 text-secondary-700 text-xs rounded-full whitespace-nowrap"
                >
                  #{tag}
                </span>
              ))}
            </div>

            {/* Disclaimer */}
            <div className="mt-8 p-4 bg-amber-50 border border-amber-200/60 rounded-xl">
              <div className="flex items-start gap-3">
                <div className="w-5 h-5 flex-shrink-0 mt-0.5 flex items-center justify-center text-amber-600">
                  <i className="ri-information-line"></i>
                </div>
                <p className="text-xs text-amber-800 leading-relaxed">
                  <strong>免责声明：</strong>本文内容仅供参考和教育目的，不能替代专业医疗诊断、治疗或建议。如有健康问题，请咨询执业医师或其他合格的医疗专业人员。
                </p>
              </div>
            </div>
          </div>
        </article>

        {/* Related Articles */}
        {relatedArticles.length > 0 && (
          <section className="w-full px-4 md:px-6 py-12 md:py-16 bg-background-100">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-8" style={{ fontFamily: 'var(--font-heading)' }}>
                相关文章
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                {relatedArticles.map((ra) => (
                  <Link
                    key={ra.slug}
                    to={`/article/${ra.slug}`}
                    className="group bg-white rounded-xl overflow-hidden border border-background-200/70 hover:border-primary-200 transition-all duration-300"
                  >
                    <div className="relative h-40 overflow-hidden">
                      <img
                        src={ra.imageUrl}
                        alt={ra.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-4">
                      <h3 className="text-sm font-semibold text-foreground-950 group-hover:text-primary-600 transition-colors line-clamp-2" style={{ fontFamily: 'var(--font-heading)' }}>
                        {ra.title}
                      </h3>
                      <p className="mt-1 text-xs text-foreground-500 line-clamp-2">{ra.excerpt}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </div>
    </>
  );
}